import React, { useCallback, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { touch } from 'redux-form';
import moment from 'moment';

import { getDateFromServer } from 'app/services/dates.service';
import { FormsEnum, CustomEventsNameEnum } from 'app/shared/enums';
import { getDateWithoutTimezoneOffset } from 'app/shared/helpers/getDateWithoutTimezoneOffset';
import { isInValidRange } from 'app/validators/validateDate';
import { useCustomEvents } from 'app/shared/hooks/useCustomEvents';

import { DateControlProps } from './DateControl.interfaces';

const DATE_FORMAT = 'DD-MM-YYYY';
const SERVER_DATE_FORMAT = 'YYYY-MM-DD';

export function useDateControl({
  minDate: propsMinDate,
  maxDate: propsMaxDate,
  formName,
  openDatePickerOnInputClick,
  input,
  onClick,
  onValueChange,
  onControlBlur,
  questionKey,
  isExternalDialogOpen,
  onRequestOpen,
  onRegisterCommit,
}: DateControlProps) {
  const { subscribeEvent, unsubscribeEvent } = useCustomEvents();
  const dispatch = useDispatch();

  const [focused, setFocused] = useState(false);
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState<string>('');
  const [minDate, setMinDate] = useState<string>();

  const handleFocus = useCallback(() => {
    setFocused(true);
  }, []);

  const setupMinDate = useCallback(async () => {
    let minDate = propsMinDate;

    if (formName === FormsEnum.TravelForm) {
      try {
        const { data } = await getDateFromServer();
        const isHourTooLate = moment(data).isSameOrAfter(
          moment(data.split('T')[0] + 'T23:30:00.000000')
        );

        if (isHourTooLate) {
          minDate = moment(minDate).add(1, 'd').format(SERVER_DATE_FORMAT);
        }
      } catch (error) {}
    }

    setMinDate(minDate);
  }, [propsMinDate, formName]);

  useEffect(() => {
    setupMinDate();
    subscribeEvent(CustomEventsNameEnum.ClearInputValue, (e: CustomEvent) => {
      setInputValue('');
    });
    return () => {
      unsubscribeEvent(CustomEventsNameEnum.ClearInputValue, () => {});
    };
  }, [unsubscribeEvent, subscribeEvent, setupMinDate]);

  const isDateValid = useCallback(
    (value: string): boolean => {
      const selectedDate = moment(value, DATE_FORMAT, true);
      const minimumDate = moment(propsMinDate, SERVER_DATE_FORMAT, true);
      const maximumDate = moment(propsMaxDate, SERVER_DATE_FORMAT, true);
      if (!selectedDate.isValid()) {
        return false;
      }

      if (minimumDate.isValid() && !selectedDate.isAfter(minimumDate)) {
        return false;
      }

      if (maximumDate.isValid() && !selectedDate.isBefore(maximumDate)) {
        return false;
      }

      return true;
    },
    [propsMinDate, propsMaxDate]
  );

  const handleBlur = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const {
        target: { value },
      } = event;

      const isValid = isDateValid(value) || value === '';

      setFocused(false);

      if (value.length === 10) {
        const serverValue = value.split('-').reverse().join('-');
        input.onBlur(serverValue);
      } else {
        input.onBlur('');
      }
      if (onControlBlur && !open && !isExternalDialogOpen) {
        onControlBlur(isValid);
      }
    },
    [onControlBlur, open, isExternalDialogOpen, isDateValid, input]
  );

  const handleClickOpen = useCallback(() => {
    if (onRequestOpen) {
      onRequestOpen();
      return;
    }

    setOpen(true);
  }, [onRequestOpen]);

  const handleClick = useCallback(() => {
    setupMinDate();

    if (openDatePickerOnInputClick) {
      handleClickOpen();
    }

    if (onClick) {
      onClick();
    }
  }, [onClick, openDatePickerOnInputClick, handleClickOpen, setupMinDate]);

  const handleValidDateTyped = useCallback(
    (value: string) => {
      const serverValue = value.split('-').reverse().join('-');

      input.onChange(serverValue);

      const shouldPatch = isInValidRange(
        propsMinDate,
        propsMaxDate,
        serverValue
      );
      if (onValueChange) {
        onValueChange(serverValue, true, shouldPatch);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [input.onChange, propsMinDate, propsMaxDate, onValueChange]
  );

  const handleInvalidDateTyped = useCallback(() => {
    if (input.value) {
      input.onChange('');
      if (onValueChange) {
        onValueChange('', false, false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input.value, input.onChange, onValueChange]);

  const clearDate = useCallback(() => {
    handleInvalidDateTyped();
    setInputValue('');
  }, [handleInvalidDateTyped]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;

      setInputValue(value);
      if (value.length === 10) {
        handleValidDateTyped(value);
      } else {
        handleInvalidDateTyped();
      }
    },
    [handleValidDateTyped, handleInvalidDateTyped]
  );

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const commitValue = useCallback(
    (value: string) => {
      formName && dispatch(touch(formName, questionKey));

      input.onChange(value);
      setInputValue('');

      if (onValueChange) {
        onValueChange(value, true, true);
      }
    },
    [formName, dispatch, questionKey, input, onValueChange]
  );

  const commitDate = useCallback(
    (date: Date) => {
      date = getDateWithoutTimezoneOffset(date, true);

      const momentDate = moment(date);

      commitValue(momentDate.format(SERVER_DATE_FORMAT).toString());
    },
    [commitValue]
  );

  const calendarChange = useCallback(
    (date: Date) => {
      handleClose();
      commitDate(date);
    },
    [handleClose, commitDate]
  );

  const getInputValue = useCallback(() => {
    let value = input.value;

    if (moment(input.value, SERVER_DATE_FORMAT, true).isValid()) {
      value = moment(input.value, SERVER_DATE_FORMAT)
        .format(DATE_FORMAT)
        .toString();
      return value;
    }

    return inputValue;
  }, [input.value, inputValue]);

  useEffect(() => {
    if (!onRegisterCommit) {
      return;
    }

    onRegisterCommit({
      commit: commitValue,
      clear: clearDate,
      resolvedMinDate: minDate,
    });
  }, [onRegisterCommit, commitValue, clearDate, minDate]);

  const getCalendarValue = useCallback(() => {
    let value = input.value;

    if (!moment(value, SERVER_DATE_FORMAT).isValid()) {
      return undefined;
    }

    return getDateWithoutTimezoneOffset(new Date(value));
  }, [input.value]);

  return {
    minDate,
    open,
    focused,
    calendarChange,
    handleClose,
    getCalendarValue,
    handleChange,
    handleBlur,
    handleFocus,
    getInputValue,
    handleClickOpen,
    handleClick,
  };
}
